/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;
      this.showStockNumbers = this.dataset.showStockNumbers === 'true';
      this.stateColors = null;
      this.buttonColors = {
        comingSoonBg: this.dataset.buttonColorComingSoon,
        comingSoonText: this.dataset.buttonTextComingSoon
      };

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
        this.updateOptionStockIndicators();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      this.updateOptionStockIndicators();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates stock badges rendered under option selectors.
     */
    updateOptionStockIndicators() {
      if (!this.optionStockElements) {
        this.optionStockElements = this.querySelectorAll('.js-option-stock');
      }

      if (!this.optionStockElements.length) return;

      const selectedOptions = this.getSelectedOptions();

      this.optionStockElements.forEach((stockEl) => {
        const optionIndex = Number(stockEl.dataset.optionIndex);
        const optionValue = stockEl.dataset.optionValue;
        const variant = this.resolveVariantForOption(optionIndex, optionValue, selectedOptions);

        if (variant) {
          const formatted = this.data.formatted[variant.id];
          const status = formatted?.stockStatus || '';
          const shouldShowIndicator = Boolean(status);

          const stockHtml = shouldShowIndicator ? this.buildStockIndicatorHtml(formatted) : '';
          if (stockHtml) {
            stockEl.innerHTML = stockHtml;
            stockEl.hidden = false;
          } else {
            stockEl.innerHTML = '';
            stockEl.hidden = true;
          }
          stockEl.dataset.stockStatus = status;

          const labelEl = stockEl.previousElementSibling;
          const inputEl = labelEl ? labelEl.previousElementSibling : null;
          const isComingSoon = status === 'coming_soon';
          const shouldStrike = status === 'sold_out';

          if (labelEl) {
            labelEl.classList.toggle('is-coming-soon', isComingSoon);
            if (status) {
              labelEl.setAttribute('title', this.resolveStockLabel(status) || '');
            } else {
              labelEl.removeAttribute('title');
            }
          }
          if (inputEl) {
            inputEl.classList.toggle('is-coming-soon', isComingSoon);
            if (shouldStrike) {
              inputEl.classList.add('is-unavailable');
            } else {
              inputEl.classList.remove('is-unavailable');
            }
          }
        } else {
          stockEl.innerHTML = '';
          stockEl.hidden = true;
          stockEl.dataset.stockStatus = '';

          const labelEl = stockEl.previousElementSibling;
          const inputEl = labelEl ? labelEl.previousElementSibling : null;
          if (labelEl) {
            labelEl.classList.remove('is-coming-soon');
            labelEl.removeAttribute('title');
          }
          if (inputEl) {
            inputEl.classList.remove('is-coming-soon');
            inputEl.classList.remove('is-unavailable');
          }
        }
      });
    }

    /**
     * Finds the variant that matches the current selection with a fixed option value.
     * @param {number} optionIndex
     * @param {string} optionValue
     * @param {string[]} selectedOptions
     * @returns {?object}
     */
    resolveVariantForOption(optionIndex, optionValue, selectedOptions) {
      const tentativeSelection = [...selectedOptions];
      tentativeSelection[optionIndex] = optionValue;

      const variantMatch = this.data.product.variants.find((variant) => {
        return variant.options.every((value, index) => {
          if (index === optionIndex) {
            return value === optionValue;
          }
          const selected = tentativeSelection[index];
          if (selected === null || selected === undefined) {
            return true;
          }
          return value === selected;
        });
      });

      if (variantMatch) return variantMatch;

      return this.data.product.variants.find((variant) => variant.options[optionIndex] === optionValue) || null;
    }

    /**
     * Builds the HTML string for a stock indicator block.
     * @param {?object} formatted
     * @returns {string}
     */
    buildStockIndicatorHtml(formatted) {
      if (!formatted) return '';

      const {
        inventoryLevel,
        stockStatus,
        inventoryQuantity,
        nextAvailable
      } = formatted;

      if (!stockStatus || stockStatus === 'none') return '';
      //if (stockStatus === 'catalog_unavailable') return '';

      const level = inventoryLevel || this.resolveInventoryLevelClass(stockStatus);
      if (!level) return '';

      const showNumbers = this.showStockNumbers;
      let quantityHtml = '';
      let assistiveHtml = '';
      const color = this.getColorForStatus(stockStatus);
      const styleAttr = color
        ? ` style="--stock-icon-bg:${color};--stock-text-color:${color};"`
        : '';

      if (showNumbers) {
        if (stockStatus === 'available' || stockStatus === 'low') {
          if (typeof inventoryQuantity === 'number' && inventoryQuantity > 0) {
            quantityHtml = `<span class="stock__text">${inventoryQuantity}</span>`;
          }
        } else if (stockStatus === 'coming_soon') {
          if (typeof nextAvailable === 'number' && nextAvailable > 0) {
            quantityHtml = `<span class="stock__text">${nextAvailable}</span>`;
          }
          assistiveHtml = this.buildAssistiveText(this.dataset.stockLabelSoon);
        } else if (stockStatus === 'sold_out' || stockStatus === 'catalog_unavailable') {
          const soldOutLabel = this.dataset.stockLabelSoldOut;
          const catalogLabel = this.dataset.stockLabelCatalog || soldOutLabel;
          assistiveHtml = this.buildAssistiveText(
            stockStatus === 'catalog_unavailable' ? catalogLabel : soldOutLabel
          );
        }
      } else {
        assistiveHtml = this.buildAssistiveText(this.resolveStockLabel(stockStatus));
      }

      return `<span class="stock stock__${level}"${styleAttr}><span class="stock__icon"></span>${quantityHtml}${assistiveHtml}</span>`;
    }

    /**
     * Resolves the label to show for a stock status.
     * @param {string} status
     * @returns {string}
     */
    resolveStockLabel(status) {
      const map = {
        available: this.dataset.stockLabelAvailable,
        low: this.dataset.stockLabelLow,
        coming_soon: this.dataset.stockLabelSoon,
        sold_out: this.dataset.stockLabelSoldOut,
        catalog_unavailable: this.dataset.stockLabelCatalog || this.dataset.stockLabelSoldOut
      };

      return map[status] || '';
    }

    /**
     * Gets configured color for a stock status.
     * @param {string} status
     * @returns {?string}
     */
    getColorForStatus(status) {
      if (!this.stateColors) {
        this.stateColors = {
          available: this.dataset.colorAvailable,
          low: this.dataset.colorLow,
          coming_soon: this.dataset.colorComingSoon,
          sold_out: this.dataset.colorSoldOut,
          catalog_unavailable: this.dataset.colorCatalog || this.dataset.colorSoldOut
        };
      }

      return this.stateColors[status] || '';
    }

    /**
     * Fallback CSS class for stock indicator when not provided.
     * @param {string} status
     * @returns {string}
     */
    resolveInventoryLevelClass(status) {
      const map = {
        available: 'normal',
        low: 'very_low',
        coming_soon: 'preorder',
        sold_out: 'sold_out',
        catalog_unavailable: 'sold_out'
      };

      return map[status] || '';
    }

    /**
     * Builds visually hidden assistive text when needed.
     * @param {string} text
     * @returns {string}
     */
    buildAssistiveText(text = '') {
      if (!text) return '';
      return `<span class="stock__text visually-hidden">${text}</span>`;
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const formatted = this.variant ? this.data.formatted[this.variant.id] : null;
      const availabilityState = formatted ? formatted.availability : null;
      const stockStatus = formatted ? formatted.stockStatus : null;
      const isCatalogUnavailable = !!(formatted && (formatted.catalogUnavailable || stockStatus === 'catalog_unavailable'));
      const forcedUnavailableStates = ['coming_soon', 'sold_out', 'catalog_unavailable'];
      const isForcedUnavailable = forcedUnavailableStates.includes(availabilityState) || isCatalogUnavailable;
      const canPurchase = variantAvailable && !this.preSelection && !isForcedUnavailable;

      this.lowStockNotice = this.lowStockNotice || this.productForm.querySelector('.js-low-stock-message');
      if (this.lowStockNotice) {
        if (stockStatus === 'low' && !this.preSelection) {
          this.lowStockNotice.textContent = theme.strings.lowStock || '';
          this.lowStockNotice.hidden = false;
        } else {
          this.lowStockNotice.hidden = true;
        }
      }

      if (this.addBtn && !this.addBtn.dataset.originalBg) {
        const computed = window.getComputedStyle(this.addBtn);
        this.addBtn.dataset.originalBg = computed.background;
        this.addBtn.dataset.originalBgColor = computed.backgroundColor;
        this.addBtn.dataset.originalBorder = computed.borderColor;
        this.addBtn.dataset.originalText = computed.color;
      }

      if (availabilityState === 'coming_soon' && this.addBtn) {
        const bg = this.buttonColors.comingSoonBg || this.getColorForStatus('coming_soon');
        const textColor = this.buttonColors.comingSoonText || '#ffffff';
        if (bg) {
          this.addBtn.style.background = bg;
          this.addBtn.style.borderColor = bg;
          this.addBtn.style.color = textColor;
        }
      } else if (this.addBtn) {
        this.addBtn.style.background = this.addBtn.dataset.originalBg || '';
        if (this.addBtn.dataset.originalBgColor) {
          this.addBtn.style.backgroundColor = this.addBtn.dataset.originalBgColor;
        }
        this.addBtn.style.borderColor = this.addBtn.dataset.originalBorder || '';
        this.addBtn.style.color = this.addBtn.dataset.originalText || '';
      }

      if (!this.addBtn) return;
      this.addBtn.disabled = !canPurchase;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {
        let unavailableStr;

        if (!this.variant) {
          unavailableStr = theme.strings.noVariant;
        } else if (isCatalogUnavailable) {
          unavailableStr = theme.strings.catalogUnavailable || theme.strings.noStock;
        } else if (availabilityState === 'coming_soon') {
          unavailableStr = (theme.strings && theme.strings.comingSoon)
            ? theme.strings.comingSoon
            : theme.strings.noStock;
        } else {
          unavailableStr = theme.strings.noStock;
        }

        this.addBtn.textContent = canPurchase
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
        
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      const messages = {
        available: this.backorder.dataset.messageAvailable,
        coming_soon: this.backorder.dataset.messageComingSoon,
        sold_out: this.backorder.dataset.messageSoldOut
      };
      let availabilityState = this.backorder.dataset.state || 'hidden';
      let message = '';

      if (this.variant) {
        const formatted = this.data.formatted[this.variant.id] || {};
        availabilityState = formatted.availability || 'hidden';
      }

      if (messages[availabilityState]) {
        message = messages[availabilityState];
      }

      const hideBackorder = !message;

      this.backorder.hidden = hideBackorder;
      this.backorder.dataset.state = availabilityState;

      const messageEl = this.backorder.querySelector('.backorder__message');
      if (messageEl) {
        messageEl.textContent = message || '';
      }

      if (!hideBackorder && this.variant) {
        const backorderProdEl = this.backorder.querySelector('.backorder__product');
        if (backorderProdEl) {
          const prodTitleEl = this.section.querySelector('.product-title');
          const productTitle = prodTitleEl ? prodTitleEl.textContent : '';
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${productTitle}${variantTitle}`;
        }
      }
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        const formattedVariant = this.data.formatted[this.variant.id] || {};
        const isOnSale = typeof formattedVariant.isOnSale === 'boolean'
          ? formattedVariant.isOnSale
          : this.variant.compare_at_price > this.variant.price;
        this.price.classList.toggle('price--on-sale', isOnSale);
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
